import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Box,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import {
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  PersonOutline as PersonOutlineIcon,
} from "@mui/icons-material";
import { blue, red, grey } from "@mui/material/colors";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const UserList = ({
  users,
  loading,
  pagination,
  onViewUser,
  onBanUser,
  onPreviousPage,
  onNextPage,
}) => {
  const getAccountTypeColor = (accountType) => {
    switch (accountType?.toLowerCase()) {
      case "basic":
        return "default";
      case "standard":
        return "primary";
      case "premium":
        return "secondary";
      case "elite":
        return "warning";
      default:
        return "default";
    }
  };

  const getAccountTypeIcon = (accountType) => {
    switch (accountType?.toLowerCase()) {
      case "elite":
        return "👑";
      case "premium":
        return "💎";
      case "standard":
        return "⭐";
      default:
        return "";
    }
  };

  const getStatusColor = (isOnline) => {
    return isOnline ? "success" : "default";
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return "Never";
    try {
      return new Date(lastSeen).toLocaleString();
    } catch {
      return "Invalid date";
    }
  };

  const formatJoinDate = (createdAt) => {
    if (!createdAt) return "Unknown";
    try {
      return new Date(createdAt).toLocaleDateString();
    } catch {
      return "Invalid date";
    }
  };

  if (loading && users.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }} elevation={3}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading users...
        </Typography>
      </Paper>
    );
  }

  if (users.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }} elevation={3}>
        <PersonOutlineIcon sx={{ fontSize: 48, color: grey[400], mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No users found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your search criteria or filters.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: grey[100] }}>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Account Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Coins/XP</TableCell>
              <TableCell>Last Seen</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <StyledTableRow key={user.id}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      src={user.profile_image_url}
                      sx={{
                        bgcolor: user.is_banned ? red[500] : blue[500],
                        mr: 2,
                        width: 40,
                        height: 40,
                      }}
                    >
                      {user.username?.charAt(0)?.toUpperCase() ||
                        user.email?.charAt(0)?.toUpperCase() ||
                        "U"}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {user.username || "No username"}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {!!user.is_verified && (
                          <Chip
                            label="Verified"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        )}
                        {!!user.is_banned && (
                          <Chip
                            label="Banned"
                            size="small"
                            color="error"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{user.email}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <span>{getAccountTypeIcon(user.account_type)}</span>
                        <span>
                          {user.account_type?.toUpperCase() || "BASIC"}
                        </span>
                      </Box>
                    }
                    size="small"
                    color={getAccountTypeColor(user.account_type)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.is_online ? "Online" : "Offline"}
                    size="small"
                    color={getStatusColor(user.is_online)}
                    variant={user.is_online ? "filled" : "outlined"}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      💰 {user.available_coins?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      ⚡ {user.available_xp?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {formatLastSeen(user.last_seen)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {formatJoinDate(user.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onViewUser(user.id)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {user.is_banned ? (
                      <Tooltip title="Unban User">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => onBanUser(user, "unban")}
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Ban User">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onBanUser(user, "ban")}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {users.length} of {pagination.total?.toLocaleString() || 0}{" "}
          users
          {pagination.pageNo && pagination.totalPages && (
            <span>
              {" "}
              (Page {pagination.pageNo} of {pagination.totalPages})
            </span>
          )}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            disabled={!pagination.hasPrev || loading}
            variant="outlined"
            size="small"
            onClick={onPreviousPage}
          >
            Previous
          </Button>
          <Button
            disabled={!pagination.hasNext || loading}
            variant="outlined"
            size="small"
            onClick={onNextPage}
          >
            Next
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default UserList;
